import { useState } from 'react';
import {
  dateToString,
  formatBlocks,
  getEffectiveBlocksForWeekday,
  isSlotBlockedByBlocks,
  minuteToTime,
} from '@/hooks/useWeeklyScheduleBase';
import { getBookingData, isBooked, isCellChangable, isOpen } from '@/utils/schedule';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const ROOM_COLOR_CONFIG = [
  {
    subHeaderHover: 'hover:bg-blue-50',
  },
  {
    subHeaderHover: 'hover:bg-teal-50',
  },
  {
    subHeaderHover: 'hover:bg-purple-50',
  },
];

export default function WeeklyScheduleGrid({
  operationPolicy,
  weekDates,
  rooms,
  filteredRooms,
  openSlotStatusMap,
  bookedStatusMap,
  timeAxis,
  sessionMinutes,
  onCellClick,
  onCellMouseDown,
  onCellMouseEnter,
  onGridMouseUp,
  onDayHeaderClick,
  onRoomHeaderClick,
  onTimeLabelClick,
}) {
  const [hoveredCell, setHoveredCell] = useState(null);

  return (
    <div className="overflow-x-auto" onMouseUp={onGridMouseUp} style={{userSelect: 'none'}}>
      <div className="inline-block min-w-full">
        <div className="flex border-b-2 border-gray-300 bg-gray-50">
          <div className="w-20 shrink-0 border-r-2 border-gray-300 sticky left-0 z-30 bg-gray-50"></div>

          {weekDates.map((date, dayIndex) => {
            const blocks = getEffectiveBlocksForWeekday(
              operationPolicy.operatingHourSettings,
              date.getDay()
            );
            const isActive = blocks.length > 0;

            return (
              <div
                key={dateToString(date)}
                className={`flex ${
                  dayIndex < weekDates.length - 1 ? 'border-r-4 border-gray-400' : ''
                }`}
                style={{ flexDirection: 'column', width: filteredRooms.length * 100 }}
              >
                <div
                  onClick={onDayHeaderClick && isActive ? () => onDayHeaderClick(date) : undefined}
                  className={`px-4 py-3 text-center transition-colors ${
                    onDayHeaderClick && isActive
                      ? 'cursor-pointer hover:bg-blue-50'
                      : isActive
                        ? ''
                        : 'opacity-40'
                  }`}
                  style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
                >
                  <div className="text-sm font-semibold text-gray-700">
                    {WEEKDAY_LABELS[date.getDay()]} {date.getDate()}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5" style={{ flex: 1 }}>
                    {formatBlocks(blocks)}
                  </div>
                </div>

                <div className="flex border-t border-gray-300">
                  {filteredRooms.map((room, roomIndex) => {
                    const colorIdx = rooms.findIndex((item) => String(item.id) === String(room.id));
                    const color = ROOM_COLOR_CONFIG[colorIdx % ROOM_COLOR_CONFIG.length];

                    return (
                      <div
                        key={room.id}
                        onClick={onRoomHeaderClick ? () => onRoomHeaderClick(date, room) : undefined}
                        className={`px-2 py-2 text-center text-xs font-medium text-gray-600 transition-colors ${
                          onRoomHeaderClick ? `cursor-pointer ${color.subHeaderHover}` : ''
                        } ${roomIndex < filteredRooms.length - 1 ? 'border-r border-gray-400' : ''}`}
                        style={{ flex: 1 }}
                      >
                        {room.roomName}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gray-50">
          {timeAxis.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">
              이번 주에 운영 시간이 설정되지 않았습니다.
            </div>
          ) : (
            timeAxis.map((minute, timeIndex) => (
              <div
                key={minute}
                className={`flex ${
                  timeIndex < timeAxis.length - 1 ? 'border-b border-gray-200' : ''
                }`}
              >
                <div
                  onClick={onTimeLabelClick ? () => onTimeLabelClick(minute) : undefined}
                  className={`w-20 shrink-0 px-3 py-2 text-sm font-medium text-gray-600 
                    bg-white border-r border-gray-200 flex items-center justify-end 
                    transition-colors sticky left-0 z-20
                    ${onTimeLabelClick ? 'cursor-pointer hover:bg-blue-50' : ''}
                    ${hoveredCell?.time === minute ? 'bg-blue-50 text-blue-700' : ''}
                  `}
                >
                  {minuteToTime(minute)}
                </div>

                {weekDates.map((date, dayIndex) => {
                  const slotDate = dateToString(date);
                  const blocks = getEffectiveBlocksForWeekday(
                    operationPolicy.operatingHourSettings,
                    date.getDay()
                  );

                  return (
                    <div
                      key={slotDate}
                      className={`flex-none flex ${
                        dayIndex < weekDates.length - 1 ? 'border-r-2 border-gray-200' : ''
                      }`}
                      style={{ width: filteredRooms.length * 100 }}
                    >
                      {filteredRooms.map((room, roomIndex) => {
                        const roomId = String(room.id);
                        const blocked = isSlotBlockedByBlocks(blocks, minute, sessionMinutes);
                        const open =
                          !blocked &&
                          isOpen({
                            openSlotStatusMap,
                            slotDate,
                            roomId: room.id,
                            minute,
                          });

                        const booked = isBooked({
                          bookedStatusMap,
                          slotDate,
                          roomId: room.id,
                          minute,
                        });

                        const bookingData = getBookingData({
                          bookedStatusMap,
                          slotDate,
                          roomId: room.id,
                          minute,
                        });

                        const isHovered =
                          hoveredCell?.dayIndex === dayIndex &&
                          hoveredCell?.time === minute &&
                          hoveredCell?.roomId === roomId;

                        const cellChangable = isCellChangable(slotDate, minute);

                        let slotCardClass =
                          'h-full w-full rounded-lg px-1 text-[11px] leading-tight font-medium transition-all flex items-center justify-center text-center';

                        if (booked || bookingData?.bookedInfo) {
                          slotCardClass += ' bg-blue-500 border border-blue-200 text-white cursor-not-allowed';
                        } else if (blocked) {
                          slotCardClass += ' bg-gray-100 border border-gray-200 text-gray-300 cursor-not-allowed';
                        } else if (!cellChangable) {
                          if (open) {
                            slotCardClass += ' bg-blue-50 border-2 border-blue-400 text-blue-700 opacity-50 cursor-not-allowed';
                          } else {
                            slotCardClass += ' bg-gray-50 border border-gray-200 text-gray-400 opacity-30 cursor-not-allowed';
                          }
                        } else if (open) {
                          slotCardClass += ' bg-blue-50 border-2 border-blue-400 text-blue-700 cursor-pointer hover:bg-blue-100';
                        } else {
                          slotCardClass += ' bg-gray-50 border border-gray-200 text-gray-400 cursor-pointer hover:bg-gray-100';
                        }

                        if (isHovered && !blocked && cellChangable && !booked) {
                          slotCardClass += ' scale-[0.98] opacity-80';
                        }

                        return (
                          <div
                            key={`${slotDate}_${room.id}_${minute}`}
                            className={`h-12 p-1 ${roomIndex < filteredRooms.length - 1 ? 'border-r border-gray-100' : ''}`}
                            onMouseDown={
                              !blocked && onCellMouseDown && cellChangable
                                ? () => onCellMouseDown(date, room, minute)
                                : undefined
                            }
                            onMouseEnter={
                              !blocked && onCellMouseEnter && cellChangable
                                ? () => onCellMouseEnter(date, room, minute)
                                : undefined
                            }
                            onMouseMove={() => setHoveredCell({ dayIndex, time: minute, roomId })}
                            onMouseLeave={() => setHoveredCell(null)}
                            style={{ flex: 1 }}
                          >
                            <div className={slotCardClass}>
                              {bookingData?.bookedInfo ? (
                                <>
                                  {bookingData.bookedInfo.customerNameMasked}
                                  <br />
                                  ({bookingData.bookedInfo.plannerName})
                                </>
                              ) : open ? (
                                '가능'
                              ) : blocked ? (
                                '-'
                              ) : (
                                '불가'
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}